/* ============================================================================
   FitLibrary — fitlibrary-reviews.js
   LANDING REVIEWS · presentation only.

   This file used to be the whole review system: it held the filtering, the
   sorting, the maths and the rendering, and it computed the headline average
   from whichever cards happened to be on screen. All of that has moved to
   assets/fitlibrary-review-source.js. What is left here is a view — it asks
   FitLibraryReviews two questions and draws the answers.

     · the global summary  -> every approved review, all ten products
     · the landing pool    -> approved AND rating >= 4.0, diversified

   Those are different sets on purpose. The number in the header counts the
   2-star reviews that the carousel below is not allowed to show. If this file
   ever computes an average itself, that separation has been broken.

   No autoplay, no marquee, no timer. The track moves only when someone presses
   an arrow, swipes, or tabs into a card on another page.

   SUBMISSION — COLLECTION MODE. The dialog now writes to the real backend
   through the Shopify App Proxy, while every card on this page still comes
   from the sample set. Those are two independent questions and this file asks
   the right one of each: submissionsEnabled() decides whether the form can be
   used, and the display never consults it. A submitted review is stored
   unpublished and has no effect on anything rendered here.
   ============================================================================ */

(function (window, document) {
  "use strict";

  function init(root) {
    if (!root || root.dataset.lrBound === "1") return;
    var SRC = window.FitLibraryReviews;
    if (!SRC) return;
    root.dataset.lrBound = "1";

    var cfg = {};
    try {
      var cfgEl = root.querySelector("[data-lr-config]");
      if (cfgEl) cfg = JSON.parse(cfgEl.textContent) || {};
    } catch (e) { cfg = {}; }

    var minRating = cfg.minRating == null ? SRC.MIN_HIGHLIGHT_RATING : Number(cfg.minRating);
    var maxReviews = Number(cfg.maxReviews) || 18;
    var diversify = cfg.diversify !== false;

    var esc = SRC.esc;

    /* ------------------------------------------------------------ summary
       Global average and count. ALL approved reviews across all ten products,
       never the highlighted subset. */
    (function renderSummary() {
      var g = SRC.globalSummary();
      var elAvg = root.querySelector("[data-lr-avg]");
      var elStars = root.querySelector("[data-lr-stars]");
      var elCount = root.querySelector("[data-lr-count]");
      if (!g.count) {
        if (elAvg) elAvg.textContent = "—";
        if (elStars) elStars.innerHTML = SRC.starsHTML(0, "Not yet rated");
        if (elCount) elCount.textContent = "No reviews yet";
        return;
      }
      if (elAvg) elAvg.textContent = g.averageDisplay;
      if (elStars) {
        elStars.innerHTML = SRC.starsHTML(g.average, g.averageDisplay + " out of 5, based on " + g.count + " review" + (g.count === 1 ? "" : "s"));
      }
      if (elCount) elCount.textContent = "Based on " + g.count + " review" + (g.count === 1 ? "" : "s");
    })();

    /* ------------------------------------------------- demo-content notice
       Shown whenever any review in the public set is sample content, so a
       placeholder can never quietly read as a customer review. */
    (function demoNotice() {
      var el = root.querySelector("[data-lr-note]");
      if (!el) return;
      var hasDemo = SRC.all().some(function (r) { return r.isDemo; });
      if (!hasDemo || !el.textContent.trim()) el.hidden = true;
    })();

    /* ------------------------------------------------------------- carousel */

    var track = root.querySelector("[data-lr-track]");
    var carousel = root.querySelector("[data-lr-carousel]");
    var foot = root.querySelector("[data-lr-foot]");
    var empty = root.querySelector("[data-lr-empty]");
    var prev = root.querySelector("[data-lr-prev]");
    var next = root.querySelector("[data-lr-next]");
    var pageLabel = root.querySelector("[data-lr-page]");
    if (!track) return;

    var TINTS = ["ivory", "sage", "blush", "mist"];
    var pool = SRC.landing({ minRating: minRating, diversify: diversify, limit: maxReviews });
    var page = 0;
    var pages = 0;

    function pageSize() {
      if (window.matchMedia("(max-width: 620px)").matches) return 1;
      if (window.matchMedia("(max-width: 980px)").matches) return 4;
      return 6;
    }

    function cardHTML(r, i) {
      var p = SRC.product(r.productHandle);
      var tint = TINTS[i % TINTS.length];
      var pillInner =
        '<span class="lr-pill__dot" aria-hidden="true"></span>' +
        esc(p ? p.name : "FitLibrary");
      /* The pill links to the exact product page when the catalogue gave a
         real URL; otherwise it stays a plain span rather than a dead link. */
      var pill = p && p.url && p.url !== "#"
        ? '<a class="lr-pill" href="' + esc(p.url) + '" style="--lr-fam:' + esc(p.accent) + '">' + pillInner + "</a>"
        : '<span class="lr-pill" style="--lr-fam:' + esc(p ? p.accent : "var(--accent)") + '">' + pillInner + "</span>";

      /* Name and age on the one existing line, in the one existing span,
         composed by the shared source. The Sample review notice below is
         untouched by this and stays where it is. */
      var who = SRC.reviewerLine ? SRC.reviewerLine(r) : r.reviewerDisplayName;

      return '<article class="nr-card nr-card--' + tint + '">' +
        (who ? '<span class="nr-card__name">' + esc(who) + "</span>" : "") +
        (r.title ? '<h3 class="nr-card__title">' + esc(r.title) + "</h3>" : "") +
        '<span class="nr-card__stars">' + SRC.starsHTML(r.rating) + "</span>" +
        (r.body ? '<p class="nr-card__body">' + esc(r.body) + "</p>" : "") +
        pill +
        (r.isDemo ? '<span class="lr-demo">Sample review</span>' : "") +
        "</article>";
    }

    function build() {
      var size = pageSize();
      pages = Math.max(1, Math.ceil(pool.length / size));
      if (page > pages - 1) page = pages - 1;
      if (page < 0) page = 0;

      var html = "";
      for (var pi = 0; pi < pages; pi++) {
        html += '<div class="lr-page" role="group" aria-roledescription="slide" aria-label="Reviews ' + (pi + 1) + " of " + pages + '">';
        for (var ci = pi * size; ci < Math.min((pi + 1) * size, pool.length); ci++) {
          html += cardHTML(pool[ci], ci);
        }
        html += "</div>";
      }
      track.innerHTML = html;
      apply();
    }

    function apply() {
      track.style.transform = "translateX(" + (-100 * page) + "%)";
      /* Cards outside the current page are removed from the tab order so
         keyboard focus cannot land on something nobody can see. */
      var kids = track.children;
      for (var i = 0; i < kids.length; i++) {
        var isCurrent = i === page;
        kids[i].setAttribute("aria-hidden", isCurrent ? "false" : "true");
        Array.prototype.forEach.call(kids[i].querySelectorAll("a"), function (a) {
          if (isCurrent) a.removeAttribute("tabindex"); else a.setAttribute("tabindex", "-1");
        });
      }
      if (prev) prev.disabled = page <= 0;
      if (next) next.disabled = page >= pages - 1;
      if (pageLabel) pageLabel.textContent = pages > 1 ? (page + 1) + " / " + pages : "";
      if (foot) foot.hidden = pages <= 1;
    }

    function go(delta) {
      var target = page + delta;
      if (target < 0 || target > pages - 1) return;
      page = target;
      apply();
    }

    if (!pool.length) {
      if (carousel) carousel.hidden = true;
      if (foot) foot.hidden = true;
      if (empty) empty.hidden = false;
    } else {
      if (empty) empty.hidden = true;
      build();
    }

    if (prev) prev.addEventListener("click", function () { go(-1); });
    if (next) next.addEventListener("click", function () { go(1); });

    track.addEventListener("keydown", function (e) {
      if (e.key === "ArrowLeft") { e.preventDefault(); go(-1); }
      else if (e.key === "ArrowRight") { e.preventDefault(); go(1); }
    });

    /* Touch / pointer swipe. Horizontal intent only, so a vertical page scroll
       is never captured. */
    (function swipe() {
      var x0 = null, y0 = null, locked = false;
      track.addEventListener("touchstart", function (e) {
        if (!e.touches || e.touches.length !== 1) return;
        x0 = e.touches[0].clientX; y0 = e.touches[0].clientY; locked = false;
      }, { passive: true });
      track.addEventListener("touchmove", function (e) {
        if (x0 == null || !e.touches || !e.touches.length) return;
        var dx = e.touches[0].clientX - x0;
        var dy = e.touches[0].clientY - y0;
        if (!locked && Math.abs(dx) > 12 && Math.abs(dx) > Math.abs(dy) * 1.4) locked = true;
      }, { passive: true });
      track.addEventListener("touchend", function (e) {
        if (x0 == null) return;
        var t = e.changedTouches && e.changedTouches[0];
        if (locked && t) {
          var dx = t.clientX - x0;
          if (Math.abs(dx) > 42) go(dx < 0 ? 1 : -1);
        }
        x0 = y0 = null; locked = false;
      });
    })();

    /* Rebuild only when the breakpoint actually changes the page size. */
    (function onResize() {
      var last = pageSize();
      var raf = 0;
      window.addEventListener("resize", function () {
        if (raf) return;
        raf = window.requestAnimationFrame(function () {
          raf = 0;
          var now = pageSize();
          if (now !== last) { last = now; page = 0; build(); }
        });
      });
    })();

    /* --------------------------------------------------------- write dialog */

    (function dialog() {
      var modal = document.getElementById("rv-modal");
      var openers = root.querySelectorAll("[data-lr-open]");
      if (!modal || !openers.length) return;

      var form = modal.querySelector("#rv-form");
      var select = modal.querySelector("[data-rv-product]");
      var confirmBox = modal.querySelector("[data-rv-confirm]");
      var submitBtn = modal.querySelector(".rv-form__submit");
      var errsum = modal.querySelector("[data-rv-errsum]");
      var live = modal.querySelector("[data-rv-rate-live]");
      var lastFocus = null;

      /* Product selector — all ten real products, from the shared catalogue.
         The value is the handle, which resolves to the canonical Shopify
         product id when a submission is built. */
      if (select) {
        var opts = '<option value="" selected disabled>Select a product…</option>';
        SRC.productOrder.forEach(function (handle) {
          var p = SRC.product(handle);
          if (p) opts += '<option value="' + esc(handle) + '">' + esc(p.name) + (p.type ? " — " + esc(p.type) : "") + "</option>";
        });
        select.innerHTML = opts;
        select.addEventListener("change", function () {
          var p = SRC.product(select.value);
          if (!confirmBox) return;
          if (!p) { confirmBox.hidden = true; return; }
          confirmBox.hidden = false;
          var n = confirmBox.querySelector("[data-rv-confirm-name]");
          var t = confirmBox.querySelector("[data-rv-confirm-type]");
          var l = confirmBox.querySelector("[data-rv-confirm-link]");
          if (n) n.textContent = p.name;
          if (t) t.textContent = p.type || "";
          if (l) l.href = p.url || "#";
          var dot = confirmBox.querySelector(".rv-confirm__dot");
          if (dot) dot.style.background = p.accent;
        });
      }

      /* ---------------------------------------------------------- rating

         THE RADIO GROUP IS THE CONTROL. Ten radios, 0.5 to 5.0, each with an
         invisible half-star label over one half of one star. Checking one is
         what selection means — by tap, by click, or by an arrow key walking
         the group — so touch and keyboard need nothing added here, and none
         of it is reachable only through :hover.

         This function does one job: paint the five stars to match whatever
         value is live. That is the previewed value while a mouse is over the
         row, the chosen value otherwise, and zero before anything is chosen —
         which is why the control now opens as five empty stars instead of
         five that look already selected. */
      (function rating() {
        var row = modal.querySelector("[data-rv-rate]");
        if (!row) return;

        var stars = Array.prototype.slice.call(row.querySelectorAll("[data-rv-star]"));
        var inputs = Array.prototype.slice.call(modal.querySelectorAll('input[name="rv-rating"]'));
        if (!stars.length || !inputs.length) return;

        /* Whatever the legend shipped with — "Select a rating" — is what the
           control falls back to, so the copy lives in the markup. */
        var DEFAULT_TEXT = live ? live.textContent : "";
        var selected = 0;
        var preview = null;

        /* A half is a real half: star n is filled by whatever of the value is
           left once the n-1 stars before it are full. 3.5 fills three stars
           and half of the fourth; nothing is rounded to a whole star. */
        function paint(value) {
          for (var i = 0; i < stars.length; i++) {
            var part = Math.max(0, Math.min(1, value - i));
            stars[i].style.setProperty("--rv-fill", (part * 100) + "%");
          }
        }

        /* 4 rather than 4.0, 4.5 stays 4.5 — the same way a card prints it. */
        function format(value) {
          return (Math.round(value * 10) / 10) + " out of 5";
        }

        function render() {
          var value = preview == null ? selected : preview;
          paint(value);
          row.setAttribute("data-rv-value", selected ? String(selected) : "");
          if (live) live.textContent = value ? format(value) : DEFAULT_TEXT;
        }

        function readSelected() {
          var checked = null;
          for (var i = 0; i < inputs.length; i++) if (inputs[i].checked) checked = inputs[i];
          selected = checked ? Number(checked.value) : 0;
        }

        for (var i = 0; i < inputs.length; i++) {
          inputs[i].addEventListener("change", function () {
            readSelected();
            /* A choice ends the preview: what is drawn from here on is the
               chosen value until the pointer moves again. */
            preview = null;
            render();
          });
        }

        /* Pointer preview, mouse only. A tap fires an emulated mouse event
           too, and honouring it would leave a phantom preview sitting on the
           stars after the finger has gone — the tap has already checked the
           radio, so touch needs no preview at all. */
        var canHover = true;
        try { canHover = window.matchMedia("(hover: hover)").matches; } catch (e) {}

        if (canHover) {
          row.addEventListener("mouseover", function (e) {
            var hit = e.target && e.target.getAttribute ? e.target : null;
            var v = hit ? hit.getAttribute("data-v") : null;
            if (v == null) return;
            preview = Number(v);
            render();
          });
          /* Leaving without clicking restores the selected value, or five
             empty stars when nothing has been selected yet. */
          row.addEventListener("mouseleave", function () {
            preview = null;
            render();
          });
        }

        readSelected();
        render();
      })();

      /* Age is optional and is digits only. The value is carried into the real
         submission below, but only when the reviewer gave one — a blank field
         sends no key at all. */
      var ageInput = modal.querySelector("[data-rv-age]");
      if (ageInput) {
        ageInput.addEventListener("input", function () {
          var digits = ageInput.value.replace(/[^0-9]/g, "").slice(0, 3);
          if (digits !== ageInput.value) ageInput.value = digits;
        });
      }

      /* THE FORM ASKS ABOUT WRITING, NOT ABOUT READING.

         This used to be SRC.isPersistent(), which is a question about where
         the CARDS get their reviews — and under Collection Mode that answer is
         deliberately false: the storefront shows the sample set and will keep
         showing it. Asking it here would disable a form whose backend is
         live. submissionsEnabled() asks the only question that matters to a
         submit button: is there a real place to send this?

         The fallback keeps an older build honest rather than optimistic: with
         no write seam at all, the disabled state is still the correct one. */
      function canSubmit() {
        return typeof SRC.submissionsEnabled === "function"
          ? SRC.submissionsEnabled()
          : SRC.isPersistent();
      }

      var persistent = canSubmit();
      var submitLabel = submitBtn ? submitBtn.textContent : "";

      /* ASKED EACH TIME THE PANEL OPENS, NOT ONCE AT START-UP.

         The write provider is a separate script, and a state computed once at
         boot would depend on which of the two ran first — a load-order rule
         somebody has to know about and keep. Re-reading it here removes the
         rule: a backend that registers before, after, or asynchronously all
         reach a form that reflects reality when the customer actually sees it.

         Both directions are handled. A connected backend restores the real
         button; a disconnected one puts back the honest disabled state rather
         than leaving a live-looking button over nothing. */
      function syncSubmitState() {
        persistent = canSubmit();

        if (submitBtn) {
          submitBtn.disabled = !persistent;
          if (persistent) {
            submitBtn.removeAttribute("aria-disabled");
            submitBtn.textContent = submitLabel;
          } else {
            submitBtn.setAttribute("aria-disabled", "true");
            submitBtn.textContent = "Submissions not yet connected";
          }
        }

        if (errsum) {
          if (persistent) {
            /* Only ever clears the not-connected notice. A validation message
               put here by a failed submission is left alone. */
            if (errsum.dataset.rvNotConnected === "1") {
              errsum.hidden = true;
              errsum.classList.remove("is-visible");
              errsum.textContent = "";
              errsum.dataset.rvNotConnected = "";
            }
          } else {
            errsum.hidden = false;
            errsum.classList.add("is-visible");
            errsum.textContent = cfg.notConnectedMessage ||
              "Review submission is not connected yet. This form is ready, but reviews cannot be saved until the review backend is live.";
            errsum.dataset.rvNotConnected = "1";
          }
        }
      }

      syncSubmitState();

      function focusables() {
        return Array.prototype.filter.call(
          modal.querySelectorAll('a[href],button:not([disabled]),select,input,textarea,[tabindex]:not([tabindex="-1"])'),
          function (el) { return el.offsetParent !== null; }
        );
      }

      /* THE SCROLL LOCK MOVED OFF <html> AND ONTO <body>.

         `document.documentElement.style.overflow = "hidden"` locks the page,
         but it also stops the viewport being a scrollport — and `position:
         sticky` resolves against the nearest scrollport. The sticky header
         group therefore snapped back to its static position the instant this
         dialog opened, which on a page scrolled a few thousand pixels down
         means the header leaves the screen entirely. It is the same trap
         assets/fitlibrary-header.js documents for the mobile menu.

         Locking `body` instead keeps the viewport as the scrollport, so the
         header stays exactly where it is stuck while the panel is open — which
         is what makes positioning the panel beneath it mean anything. Measured
         both ways at 1440 and 390: the page is equally unscrollable, and the
         header holds. The class is defined in fitlibrary-review-form.css and
         is the dialog's own; the header is not touched. */
      function lockScroll(on) {
        if (document.body) document.body.classList.toggle("rv-modal-open", !!on);
      }

      /* The panel transitions `visibility` from hidden to visible over 280ms,
         and a browser will not move focus into a subtree that still computes
         to `visibility: hidden`. The original single `.focus()` call therefore
         landed nowhere: a keyboard user opening the dialog was left outside it
         and had to tab through the whole carousel to reach the form. Retrying
         on animation frames until it takes costs nothing when it works first
         time and gives up well before it could loop. */
      function focusFirst(tries) {
        var f = focusables();
        if (!f.length) return;
        f[0].focus();
        if (document.activeElement !== f[0] && tries < 40) {
          window.requestAnimationFrame(function () { focusFirst(tries + 1); });
        }
      }

      function open(e) {
        lastFocus = e && e.currentTarget ? e.currentTarget : document.activeElement;
        /* The submission backend may have registered since this dialog was
           built. Ask again, so what the customer sees is current. */
        syncSubmitState();
        modal.setAttribute("aria-hidden", "false");
        modal.classList.add("is-open", "is-active");
        lockScroll(true);
        focusFirst(0);
      }

      function close() {
        modal.setAttribute("aria-hidden", "true");
        modal.classList.remove("is-open", "is-active");
        lockScroll(false);
        if (lastFocus && lastFocus.focus) lastFocus.focus();
      }

      Array.prototype.forEach.call(openers, function (b) { b.addEventListener("click", open); });
      Array.prototype.forEach.call(modal.querySelectorAll("[data-rv-close]"), function (b) {
        b.addEventListener("click", close);
      });
      modal.addEventListener("click", function (e) { if (e.target === modal) close(); });
      modal.addEventListener("keydown", function (e) {
        if (e.key === "Escape") { e.preventDefault(); close(); return; }
        if (e.key !== "Tab") return;
        var f = focusables();
        if (!f.length) return;
        var first = f[0], last = f[f.length - 1];
        if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
        else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
      });

      if (form) {
        form.addEventListener("submit", function (e) {
          e.preventDefault();
          if (!persistent) return;
          /* submit() resolves only when the backend accepted the record. The
             success panel is shown from that resolution, never optimistically,
             and what it reports is "pending" — the review was stored as an
             unpublished DRAFT, which is what Collection Mode means. */
          var checked = modal.querySelector('input[name="rv-rating"]:checked');

          var record = {
            productHandle: select ? select.value : "",
            rating: checked ? checked.value : 0,
            title: (modal.querySelector("[data-rv-title]") || {}).value,
            body: (modal.querySelector("[data-rv-body]") || {}).value,
            reviewerDisplayName: (modal.querySelector("[data-rv-name]") || {}).value,
            email: (modal.querySelector("[data-rv-email]") || {}).value,
            consent: !!(modal.querySelector("[data-rv-consent]") || {}).checked
          };

          /* Age is optional, so a blank field sends no key at all rather than
             an empty one. The backend distinguishes "did not say" from "said
             nothing"; an empty string would erase that distinction here, in
             the one place where it is still knowable. */
          var ageField = modal.querySelector("[data-rv-age]");
          var ageValue = ageField ? String(ageField.value || "").trim() : "";
          if (ageValue !== "") record.age = ageValue;

          SRC.submit(record).then(function () {
            var success = modal.querySelector("[data-rv-success]");
            form.hidden = true;
            if (success) {
              success.hidden = false;
              var b = success.querySelector("button");
              if (b) b.focus();
            }
          }).catch(function (err) {
            if (errsum) {
              errsum.hidden = false;
              errsum.classList.add("is-visible");
              errsum.textContent = err && err.message ? err.message : "Your review could not be submitted.";
              errsum.dataset.rvNotConnected = "";
              errsum.focus();
            }
          });
        });
      }
    })();
  }

  function boot() {
    Array.prototype.forEach.call(document.querySelectorAll("[data-lr-reviews]"), init);
  }

  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", boot);
  else boot();

  /* Theme editor: re-init when the section is re-rendered. */
  document.addEventListener("shopify:section:load", function (e) {
    var r = e.target.querySelector ? e.target.querySelector("[data-lr-reviews]") : null;
    if (r) { r.dataset.lrBound = ""; init(r); }
  });
})(window, document);